import { useCallback, useEffect, useRef } from 'react';
import { useSetAtom } from 'jotai';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
	startStreamingAtom,
	endStreamingAtom,
	setStreamingStatusAtom,
	appendContentAtom,
	replaceContentAtom,
	appendThinkingAtom,
	addActivityAtom,
	setCitationsAtom,
	setTokenUsageAtom,
	setSuggestionsAtom
} from '../atoms/streamingAtoms';
import { setInterruptAtom } from '../atoms/interruptAtoms';
import { tokenLimitDialogAtom } from '../atoms/tokenLimitAtom';
import { queryKeys } from '../queries/queryKeys';
import { EApiErrorCode } from '@/_constants/errorLiterals';
import type { StreamEvent, IMessage, Citation, InterruptQuestion } from '@/types';
import { AnalyticsService } from '@/_services/analytics.service.client';
import { useNotificationSound } from '@/_hooks/useNotificationSound.client';

interface StreamOptions {
	conversationId: string;
	onMessage?: (message: IMessage) => void;
	onStreamStart?: () => void;
	onComplete?: (message: IMessage) => void;
	onError?: (error: string) => void;
	/** Skip message events (used when messages are added optimistically) */
	skipMessageEvents?: boolean;
	/** Skip stream_start event (used when placeholder is added optimistically) */
	skipStreamStart?: boolean;
	/** Suppress concurrent request lock errors (used during loadMessages on refresh) */
	suppressLockErrors?: boolean;
	/** Suppress notification sound (used during loadMessages — not a new AI response) */
	suppressNotification?: boolean;
}

// ===== Module-level singleton =====
// Only one stream can be active at a time (enforced by activeConversation guard).
// Module-level variables allow any code (including useStopGeneration) to abort the current stream.
let currentAbortController: AbortController | null = null;
let currentActiveConversation: string | null = null;
// Separate boolean flag for abort — survives controller nullification.
// processStream checks this flag to know when to stop processing events.
let isAborted = false;

/**
 * Abort the currently active SSE stream (if any).
 * This only stops client-side event consumption — the server continues processing.
 * Exported so useStopGeneration can call it without needing the hook instance.
 */
export function abortCurrentStream() {
	isAborted = true;
	currentAbortController?.abort();
	currentAbortController = null;
	currentActiveConversation = null;
}

/** Enrich stream_end message with streaming-only data (citations, suggestions) */
function enrichEndMessage(message: IMessage, streamedCitations: Citation[], streamedSuggestions: string[]): IMessage {
	if (streamedCitations.length === 0 && streamedSuggestions.length === 0) return message;
	return {
		...message,
		...(streamedCitations.length > 0 && { citations: streamedCitations }),
		...(streamedSuggestions.length > 0 && { suggestions: streamedSuggestions })
	};
}

/**
 * Hook to handle SSE streaming for messages
 *
 * Data Flow:
 * 1. 'message' events → upsert to TanStack Query cache (replaces optimistic or appends)
 * 2. 'stream_start' → initialize Jotai streaming state
 * 3. 'content_chunk' → update Jotai ONLY (no cache update)
 * 4. 'status', 'agent_activity', 'evidence', 'token_usage' → update Jotai
 * 5. 'stream_end' → commit final message to TanStack Query cache, reset Jotai
 *
 * During streaming, components read from Jotai atoms (streamingMessageAtom).
 * After streaming, components read from TanStack Query cache.
 */
export const useStreamingMessage = () => {
	const queryClient = useQueryClient();

	// Jotai setters for streaming state
	const startStreaming = useSetAtom(startStreamingAtom);
	const endStreaming = useSetAtom(endStreamingAtom);
	const setStatus = useSetAtom(setStreamingStatusAtom);
	const appendContent = useSetAtom(appendContentAtom);
	const replaceContent = useSetAtom(replaceContentAtom);
	const appendThinking = useSetAtom(appendThinkingAtom);
	const addActivity = useSetAtom(addActivityAtom);
	const setCitations = useSetAtom(setCitationsAtom);
	const setTokenUsage = useSetAtom(setTokenUsageAtom);
	const setSuggestions = useSetAtom(setSuggestionsAtom);
	const setTokenLimitDialog = useSetAtom(tokenLimitDialogAtom);
	const setInterrupt = useSetAtom(setInterruptAtom);
	const { playNotification } = useNotificationSound();
	// Ref-forward playNotification so handleStreamEnd's dep array stays stable
	const playNotificationRef = useRef(playNotification);
	useEffect(() => {
		playNotificationRef.current = playNotification;
	}, [playNotification]);

	/**
	 * Upsert a message into the TanStack Query cache.
	 * If a message with the same ID exists (e.g. interrupted → resumed), replace it.
	 * Otherwise append.
	 */
	const commitToCache = useCallback(
		(conversationId: string, message: IMessage) => {
			queryClient.setQueryData<IMessage[]>(queryKeys.messages.byConversation(conversationId), (old = []) => {
				const existingIdx = old.findIndex((m) => m.id === message.id);
				if (existingIdx !== -1) {
					const updated = [...old];
					updated[existingIdx] = message;
					return updated;
				}
				return [...old, message];
			});
		},
		[queryClient]
	);

	/** Handle stream_end: commit to cache, persist interrupt state, track analytics */
	const handleStreamEnd = useCallback(
		(
			event: StreamEvent & { type: 'stream_end' },
			conversationId: string,
			streamedCitations: Citation[],
			streamedSuggestions: string[],
			pendingInterruptQuestion: InterruptQuestion | null,
			streamStartTime: number,
			onComplete?: (message: IMessage) => void,
			suppressNotification?: boolean
		) => {
			const enrichedMessage = enrichEndMessage(event.message, streamedCitations, streamedSuggestions);
			commitToCache(conversationId, enrichedMessage);
			// Invalidate data pool so DataDesk picks up new entries
			queryClient.invalidateQueries({ queryKey: queryKeys.dataPool.byConversation(conversationId) });

			// HITL interrupt: persist question to interrupt atoms so UI remains after stream ends
			if (event.interrupted && pendingInterruptQuestion && event.interruptMessageId) {
				setInterrupt({
					question: pendingInterruptQuestion,
					messageId: event.interruptMessageId,
					conversationId
				});
			} else {
				// Track AI response completion (skip for interrupted — no real response yet)
				AnalyticsService.responseReceived({
					conversationId,
					messageId: event.message.id,
					hadCitations: streamedCitations.length > 0,
					citationCount: streamedCitations.length,
					wasFallback: streamedCitations.length === 0,
					latencyMs: Date.now() - streamStartTime,
					tokenCost: event.message.tokenUsage?.totalTokens
				});
				if (!suppressNotification) playNotificationRef.current();
			}

			onComplete?.(event.message);
			currentActiveConversation = null;
			endStreaming();
		},
		[queryClient, commitToCache, setInterrupt, endStreaming]
	);

	/** Handle SSE error events (extracted to reduce processStream cognitive complexity) */
	const handleStreamError = useCallback(
		(event: StreamEvent & { type: 'error' }, suppressLockErrors?: boolean) => {
			if (event.code === EApiErrorCode.E_TOKEN_LIMIT_EXCEEDED) {
				setTokenLimitDialog({ isOpen: true, resetAt: event.resetAt || null });
			} else if (event.code === EApiErrorCode.E_CONCURRENT_REQUEST_LIMIT && !suppressLockErrors) {
				toast.error('Please wait for your current request to complete before sending another.');
			} else if (event.code !== EApiErrorCode.E_CONCURRENT_REQUEST_LIMIT) {
				toast.error(event.error);
			}
		},
		[setTokenLimitDialog]
	);

	/**
	 * Process a stream of events from the server
	 */
	const processStream = useCallback(
		async (stream: AsyncGenerator<StreamEvent, void, unknown>, options: StreamOptions) => {
			const { conversationId, onMessage, onStreamStart, onComplete, onError, suppressNotification } = options;
			let receivedEndEvent = false;
			let streamedCitations: Citation[] = [];
			let pendingInterruptQuestion: InterruptQuestion | null = null;
			let streamedSuggestions: string[] = [];
			const streamStartTime = Date.now();

			try {
				for await (const event of stream) {
					// Check if aborted — uses module-level boolean that survives controller nullification
					if (isAborted) {
						break;
					}

					switch (event.type) {
						case 'conversation_created':
							// Cache the newly created conversation and refresh sidebar
							queryClient.setQueryData(queryKeys.conversations.detail(event.conversation.id), event.conversation);
							queryClient.invalidateQueries({ queryKey: queryKeys.conversations.root });
							break;

						case 'message':
							if (!options.skipMessageEvents) {
								// Upsert: replace optimistic message if one exists, otherwise append
								queryClient.setQueryData<IMessage[]>(queryKeys.messages.byConversation(conversationId), (old = []) => {
									const optimisticIdx = old.findIndex(
										(m) => m.id.startsWith('optimistic-') && m.role === event.message.role
									);
									if (optimisticIdx !== -1) {
										const updated = [...old];
										updated[optimisticIdx] = event.message;
										return updated;
									}
									return [...old, event.message];
								});
								onMessage?.(event.message);
							}
							break;

						case 'stream_start':
							// Skip if we already started
							if (!options.skipStreamStart) {
								onStreamStart?.();
							}
							break;

						case 'status':
							// Update Jotai only
							setStatus(event);
							break;

						case 'content_chunk':
							// Update Jotai ONLY - no cache update during streaming
							appendContent(event.chunk);
							break;

						case 'content_replace':
							// Replace full text (swap <<cite:...>> markers with [n] after streaming)
							replaceContent(event.text);
							break;

						case 'thinking_chunk':
							// Accumulate model thinking/reasoning text
							appendThinking(event.chunk);
							break;

						case 'agent_activity':
							addActivity(event);
							break;

						case 'evidence':
							streamedCitations = event.citations;
							setCitations(event.citations);
							break;

						case 'token_usage':
							setTokenUsage(event.tokenUsage);
							break;

						case 'stream_end':
							receivedEndEvent = true;
							handleStreamEnd(
								event,
								conversationId,
								streamedCitations,
								streamedSuggestions,
								pendingInterruptQuestion,
								streamStartTime,
								onComplete,
								suppressNotification
							);
							break;

						case 'interrupt_request':
							// Store the question — will be persisted to interrupt atoms on stream_end
							pendingInterruptQuestion = event.question;
							break;

						case 'suggestions':
							streamedSuggestions = event.suggestions;
							setSuggestions(event.suggestions);
							break;

						case 'error':
							handleStreamError(event, options.suppressLockErrors);
							onError?.(event.error);
							// Don't end streaming here - wait for stream_end which may follow
							break;

						default:
							// Unknown event type - ignore
							break;
					}
				}

				// Stream completed naturally without explicit 'stream_end' event
				// This happens when loading existing messages (no pending response)
				if (!receivedEndEvent) {
					currentActiveConversation = null;
					endStreaming();
				}
			} catch (error) {
				// Suppress errors from aborted streams — this is expected when user stops generation.
				// Check both isAborted flag and AbortError name to handle race conditions where
				// the error fires before isAborted is set (e.g., signal.abort() rejects reader.read()
				// before the module-level flag is updated).
				if (isAborted || (error instanceof DOMException && error.name === 'AbortError')) {
					return;
				}
				const errorMessage = error instanceof Error ? error.message : 'Stream processing failed';
				toast.error(errorMessage);
				onError?.(errorMessage);
				// Recover: server may have saved a partial/error message before the connection dropped
				queryClient.invalidateQueries({ queryKey: queryKeys.messages.byConversation(conversationId) });
				currentActiveConversation = null;
				endStreaming();
			}
		},
		[
			queryClient,
			handleStreamEnd,
			handleStreamError,
			appendContent,
			replaceContent,
			appendThinking,
			addActivity,
			setCitations,
			setTokenUsage,
			setSuggestions,
			setStatus,
			endStreaming
		]
	);

	/**
	 * Start processing a message stream.
	 *
	 * The streamGenerator receives an AbortSignal that is wired to the module-level
	 * AbortController. When abortCurrentStream() is called (e.g., user clicks Stop),
	 * the signal cancels the underlying fetch() — closing the HTTP connection and
	 * stopping Vercel serverless billing. Without this, abort only stops client-side
	 * event consumption while the server continues processing.
	 *
	 * @param streamGenerator - Function that receives an AbortSignal and returns the async generator
	 * @param options - Configuration options for the stream
	 * @param beforeStart - Optional callback that runs only when stream actually starts (after guard check)
	 */
	const startMessageStream = useCallback(
		(
			streamGenerator: (signal: AbortSignal) => Promise<AsyncGenerator<StreamEvent, void, unknown>>,
			options: StreamOptions,
			beforeStart?: () => void
		) => {
			// Synchronous guard: skip if already streaming for this conversation
			// This prevents Strict Mode double-calls since module-level updates are synchronous
			if (currentActiveConversation === options.conversationId) {
				return;
			}

			// Abort any existing stream for a different conversation
			currentAbortController?.abort();
			currentAbortController = new AbortController();

			// Reset abort flag for the new stream
			isAborted = false;

			// Set synchronous guard immediately
			currentActiveConversation = options.conversationId;

			// Run beforeStart callback (e.g., clear messages cache)
			// This only runs if we passed the guard check
			beforeStart?.();

			// Initialize streaming state (Jotai)
			startStreaming(options.conversationId);

			// Start processing — pass abort signal so fetch() can be cancelled
			streamGenerator(currentAbortController!.signal)
				.then((stream) => {
					processStream(stream, options);
				})
				.catch((error) => {
					toast.error(`Failed to start stream: ${error}`);
					currentActiveConversation = null;
					endStreaming();
				});
		},
		[startStreaming, endStreaming, processStream]
	);

	/**
	 * Abort the current stream
	 */
	const abortStream = useCallback(() => {
		abortCurrentStream();
		endStreaming();
	}, [endStreaming]);

	return {
		startMessageStream,
		abortStream
	};
};
